// src/pages/WhichCar.jsx

import React, { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import { BiEdit } from "react-icons/bi";
import { useAuth } from "../context/AuthContext";
import api from "../api/axios";
import PageImageSelector from "../components/ui/PageImageSelector";
import LoadingSpinner from "../components/LoadingSpinner";
import whichCar from "../assets/which-car-should-i-buy/whichcar-new.png";
import wechatIcon from "../assets/which-car-should-i-buy/chat.png";
import sparkleIcon from "../assets/which-car-should-i-buy/sparkle.png";
import guaranteeIcon from "../assets/which-car-should-i-buy/guarantee.png";

export default function WhichCar() {
  const { user } = useAuth();
  const isAdmin = user?.roles.includes("super-admin");

  const [pageContent, setPageContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editingSection, setEditingSection] = useState(null);

  const iconMap = {
    wechat: wechatIcon,
    sparkle: sparkleIcon,
    guarantee: guaranteeIcon,
  };

  // Ref for hidden file input
  const heroFileInputRef = useRef(null);

  useEffect(() => {
    api
      .get("/pages/which-car")
      .then((res) => {
        setPageContent(res.data);
        setLoading(false);
      })
      .catch((err) => {
        console.error(err);
        setError("Cannot load page content");
        setLoading(false);
      });
  }, []);

  if (loading) return <LoadingSpinner />;
  if (error) return <p className="text-red-600">{error}</p>;
  if (!pageContent) return <p>No content</p>;

  async function saveContent(updatedContent) {
    try {
      await api.put("/pages/which-car", { content: updatedContent });
      setPageContent(updatedContent);
      toast.success("Saved successfully");
      setEditingSection(null);
    } catch (e) {
      console.error(e);
      toast.error("Failed to save");
    }
  }

  async function uploadPageImage(file, onUrlReceived) {
    const form = new FormData();
    form.append("pageKey", "which-car");
    form.append("images", file);

    try {
      const res = await api.post("/page-images", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const newImg = res.data[0];
      onUrlReceived(newImg.url);
    } catch (err) {
      console.error("Upload failed:", err);
      toast.error("Image upload failed");
    }
  }

  // Destructure hero
  const hero = pageContent.hero || {
    title: "",
    subtitle: "",
    buttonText: "",
    buttonHref: "",
    imageUrl: "",
  };

  // Ensure sections is always an array
  // const allSections = pageContent.sections || [];
  // const howItWorksSection = allSections.find(
  //   (s) => s.key === "how-it-works"
  // ) || {
  //   key: "how-it-works",
  //   heading: "",
  //   items: [
  //     { icon: "wechat", stepLabel: "Step 1", title: "", description: "" },
  //     { icon: "sparkle", stepLabel: "Step 2", title: "", description: "" },
  //     { icon: "guarantee", stepLabel: "Step 3", title: "", description: "" },
  //   ],
  //   buttonText: "",
  //   buttonHref: "",
  // };

  // hard-coded steps
  const steps = [
    {
      icon: "wechat",
      stepLabel: "Step 1",
      title: "You tell us your needs",
      description:
        "Whether you need something spacious or compact, for the open road or the school run.",
    },
    {
      icon: "sparkle",
      stepLabel: "Step 2",
      title: "We do the clever bit",
      description:
        "Our tool compares your answers against our huge range of makes and models in seconds to find ones suited to you.",
    },
    {
      icon: "guarantee",
      stepLabel: "Step 3",
      title: "You get recommendations",
      description:
        "We'll create a shortlist of recommended cars you can buy online in a matter of clicks.",
    },
  ];

  return (
    <div className="w-full">
      {/* ─── HERO SECTION ───────────────────────────────────────────────────────── */}
      <section className="relative w-full h-[60vh] md:h-[80vh] flex items-end text-white overflow-hidden">
        {/* Background image */}
        <img
          src={whichCar}
          alt="Hero background"
          className="absolute inset-0 w-full h-full object-fill z-0"
        />

        {/* FULL OVERLAY tint instead of gradient */}
        <div
          className="absolute inset-0 z-10"
          style={{
            background:
              "linear-gradient(rgba(31, 41, 84, 0.7) 10%, rgba(239, 81, 75, 0.7) 77%)",
          }}
        />

        {/* Text content */}
        <div className="relative z-20 p-6 md:p-20 mb-20">
          <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold mb-4">
            ZORQK your search.
          </h1>
          <p className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold">
            The future of car searching
          </p>
        </div>
      </section>

      {/* Need help finding your next car?  */}
      <section className="relative -mt-20 bg-[#1c2553] text-white py-20 rounded-t-3xl md:rounded-t-[50px] z-30">
        {isAdmin && editingSection !== "hero" && (
          <BiEdit
            onClick={() => setEditingSection("hero")}
            className="absolute top-4 right-4 text-2xl text-brandAccent cursor-pointer hover:text-accent"
            title="Edit Hero"
          />
        )}
        <div className="max-w-5xl mx-auto px-4 flex flex-col-reverse lg:flex-row items-center gap-10">
          <div className="lg:w-1/2 mt-10 lg:mt-0">
            {editingSection === "hero" ? (
              <>
                <input
                  type="text"
                  className="text-primary w-full mb-2 px-3 py-2 text-4xl font-extrabold rounded"
                  value={hero.title}
                  onChange={(e) =>
                    setPageContent((prev) => ({
                      ...prev,
                      hero: { ...prev.hero, title: e.target.value },
                    }))
                  }
                />
                <textarea
                  className="text-primary w-full mb-2 px-3 py-2 rounded"
                  rows={3}
                  value={hero.subtitle}
                  onChange={(e) =>
                    setPageContent((prev) => ({
                      ...prev,
                      hero: { ...prev.hero, subtitle: e.target.value },
                    }))
                  }
                />
                <div className="flex space-x-2 mb-2">
                  <input
                    type="text"
                    className="text-primary flex-1 px-3 py-2 rounded"
                    placeholder="Button text"
                    value={hero.buttonText}
                    onChange={(e) =>
                      setPageContent((prev) => ({
                        ...prev,
                        hero: { ...prev.hero, buttonText: e.target.value },
                      }))
                    }
                  />
                  <input
                    type="text"
                    className="text-primary flex-1 px-3 py-2 rounded"
                    placeholder="Button URL"
                    value={hero.buttonHref}
                    onChange={(e) =>
                      setPageContent((prev) => ({
                        ...prev,
                        hero: { ...prev.hero, buttonHref: e.target.value },
                      }))
                    }
                  />
                </div>
                <div className="mb-2">
                  <label className="block mb-1 text-sm font-medium text-primary">
                    Hero Image
                  </label>
                  <PageImageSelector
                    pageKey="which-car"
                    value={hero.imageUrl}
                    onChange={(newUrl) => {
                      if (newUrl === "__upload_new__") {
                        heroFileInputRef.current.click();
                      } else {
                        setPageContent((prev) => ({
                          ...prev,
                          hero: { ...prev.hero, imageUrl: newUrl },
                        }));
                      }
                    }}
                  />
                  <input
                    type="file"
                    accept="image/*"
                    ref={heroFileInputRef}
                    className="hidden"
                    onChange={(e) => {
                      const file = e.target.files[0];
                      if (!file) return;
                      uploadPageImage(file, (url) => {
                        setPageContent((prev) => ({
                          ...prev,
                          hero: { ...prev.hero, imageUrl: url },
                        }));
                      });
                      e.target.value = null;
                    }}
                  />
                </div>
                <div className="flex space-x-2">
                  <button
                    className="px-4 py-2 bg-green-600 text-white rounded"
                    onClick={() => saveContent({ ...pageContent })}
                  >
                    Save
                  </button>
                  <button
                    className="px-4 py-2 bg-gray-600 text-white rounded"
                    onClick={() => setEditingSection(null)}
                  >
                    Cancel
                  </button>
                </div>
              </>
            ) : (
              <div className="flex flex-col gap-5">
                <h1 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl text-accent font-bold mb-10">
                  {hero.title}
                </h1>
                <p className="text-base md:text-lg lg:text-xl font-bold">
                  {hero.subtitle}
                </p>
                <p className="text-base md:text-lg lg:text-xl">
                  Go on, ZORQK your search.
                </p>
                <Link
                  to={hero.buttonHref}
                  className="mt-10 flex justify-center bg-accent text-white font-bold rounded-full px-16 py-2 hover:opacity-90 transition"
                >
                  {hero.buttonText}
                </Link>
              </div>
            )}
          </div>
          <div className="lg:w-1/2 flex justify-center">
            <img
              src={hero.imageUrl}
              alt={hero.title}
              className="w-full h-full object-cover"
            />
          </div>
        </div>
      </section>

      {/* ─── “HOW IT WORKS” SECTION ────────────────────────────────────────────── */}

      <section className="bg-white py-10">
        <div className="max-w-6xl mx-auto px-4 text-center">
          {/* Heading */}
          <h2 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl text-primary font-bold">
            How It Works
          </h2>

          {/* Steps */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-10 py-10">
            {steps.map(({ icon, stepLabel, title, description }, idx) => (
              <div
                key={idx}
                className="flex flex-col items-center text-center space-y-4"
              >
                <div className="flex items-center justify-center">
                  <img
                    src={iconMap[icon]}
                    alt={stepLabel}
                    className="h-12 w-12 object-contain"
                  />
                </div>
                <span className="text-sm md:text-base lg:text-lg font-semibold text-brandAccent uppercase">
                  {stepLabel}
                </span>
                <h3 className="text-sm md:text-base lg:text-lg text-primary font-bold">{title}</h3>
                <p className="text-sm md:text-base lg:text-lg text-primary font-medium">
                  {description}
                </p>
              </div>
            ))}
          </div>

          {/* Call-to-action */}
          <div>
            <Link
              to="/help-me-choose/most-important"
              className="bg-accent text-white font-bold rounded-full px-8 md:px-20 py-3 hover:bg-accent/90 transition"
            >
              Find your perfect car
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
